import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
} from "../utils";
import { BaseDbObject } from "@/lib/models/db";
import { CashSaleStatus } from "./cashSales/general";
import { AcceptedCommodity } from "./profiles";

// Base schema with common fields
export const manualPosBaseSchema = z.object({
    source_type: z.string().min(1, "Source type is required"),
    contract_number: z.string().optional(),
    contract_release: z.string().optional(),
    price: z.string().optional(),
    commodity: z.string().min(1, "Commodity is required"),
    quantity: z.string(),
    customer_id: z.string().uuid("Please select a customer"),
    trading_account_id: z.string().uuid("Please select a trading account"),
    expiry_date: z.string().optional(),
    option_month: z.string().optional(),
    note: z.string().optional(),
    crop_year: z.number().min(2000).max(2050),

    // New fields
    instrument_type: z.string().optional(),
    put_or_call: z.string().optional(),
    long_qty: z.string().optional(),
    short_qty: z.string().optional(),
});

// OTC schema - keeps existing validation
export const manualPosOtcSchema = manualPosBaseSchema.extend({
    expiry_date: z.string().min(1, "Expiry date is required"),
    quantity: z.string().min(1, "Quantity is required"),
});


export const manualPosOpenOrderPxBasisSchema = manualPosBaseSchema.extend({
    instrument_type: z.literal("px Basis"),
    option_month: z.string().min(1, "Future month is required"),
    long_qty: z.string().optional(),
    short_qty: z.string().optional(),
    price: z.string().min(1, "Price is required"),
}).refine((data) => {
    const longQty = parseFloat(data.long_qty || "0");
    const shortQty = parseFloat(data.short_qty || "0");
    return longQty > 0 || shortQty > 0;
}, {
    message: "At least one of long quantity or short quantity must be greater than 0",
    path: ["long_qty"]
});

// Open Order Future schema
export const manualPosOpenOrderFutureSchema = manualPosBaseSchema.extend({
    instrument_type: z.literal("Future"),
    option_month: z.string().min(1, "Future month is required"),
    long_qty: z.string().optional(),
    short_qty: z.string().optional(),
}).refine((data) => {
    const longQty = parseFloat(data.long_qty || "0");
    const shortQty = parseFloat(data.short_qty || "0");
    return longQty > 0 || shortQty > 0;
}, {
    message: "At least one of long quantity or short quantity must be greater than 0",
    path: ["long_qty"]
});

// Open Order Option schema
export const manualPosOpenOrderOptionSchema = manualPosBaseSchema.extend({
    instrument_type: z.literal("Option"),
    option_month: z.string().min(1, "Option month is required"),
    put_or_call: z.enum(["P", "C"], { required_error: "Put/Call selection is required" }),
    price: z.string().min(1, "Strike price is required"),
    long_qty: z.string().optional(),
    short_qty: z.string().optional(),
    expiry_date: z.string().min(1, "Expiry date is required"),
}).refine((data) => {
    const longQty = parseFloat(data.long_qty || "0");
    const shortQty = parseFloat(data.short_qty || "0");
    return longQty > 0 || shortQty > 0;
}, {
    message: "At least one of long quantity or short quantity must be greater than 0",
    path: ["long_qty"]
});

// Dynamic schema selection function
export function getFormManualClientOpenPosSchema(sourceType: string, instrumentType?: string) {
    if (sourceType === "OTC") {
        return manualPosOtcSchema;
    }

    if (sourceType === "Open Order - RJO" || sourceType === "Open Order - TT") {
        if (instrumentType === "Option") {
            return manualPosOpenOrderOptionSchema;
        }
        if (instrumentType === "px Basis") {
            return manualPosOpenOrderPxBasisSchema;
        }
        return manualPosOpenOrderFutureSchema;
    }

    return manualPosBaseSchema;
}


// Common Schemas
export const IdParamsSchema = z.object({
  id: z.string().uuid()
});

const optionalUuidSchema = z.preprocess(
  (val) => {
    // Convert falsy values to undefined
    if (!val) return undefined;
    // Keep truthy values as is
    return val;
  },
  z.union([
    z.string().uuid(),
    z.undefined()
  ])
);

// Cash Sale Schemas
export const CreateCashSaleSchema = z.object({
  status: z.nativeEnum(CashSaleStatus),

  contract_type: z.string().min(1),
  contract_number: z.string().default(""),
  contract_release: z.string().default("1"),

  location_id: optionalUuidSchema,
  customer_id: z.string().uuid(),
  market_zone_id: optionalUuidSchema,
  trader_id: z.string().uuid(),

  destination: z.string().optional(),
  washed_gain_loss: z.string().optional(),

  add_date: z.string().min(1), // YYYY-MM-DD format
  start_date: z.string().optional(), // YYYY-MM-DD format
  end_date: z.string().optional(), // YYYY-MM-DD format

  note: z.string().optional(),
  commodity: z.nativeEnum(AcceptedCommodity),
  crop_year: z.number(),

  option_month: z.string().optional(),

  loads: z.string().optional(),

  quantity: z.string().min(1), // Required, not optional
  scheduled_quantity: z.string().optional(),
  balance: z.string().optional(),
  futures: z.string().optional(),
  basis: z.string().optional(),
  price: z.string().optional(),
}).refine((data) => {
  // Market zone is required unless it's a DirectSale
  if (data.contract_type !== 'DirectSale' && !data.market_zone_id) {
    return false;
  }
  return true;
}, {
  message: "Market Zone is required for non-DirectSale contracts",
  path: ["market_zone_id"]
});

export const PriceCashSaleSchema = z.object({
  base_id: z.string().uuid(),
  priced_quantity: z.string(),
  futures: z.string().optional(),
  basis: z.string().optional(),
  option_month: z.string().optional(),
  market_zone_id: z.string().uuid().optional(),
  start_date: z.string().optional(), // YYYY-MM-DD format
  end_date: z.string().optional(), // YYYY-MM-DD format
});

export const UpdateCashSaleSchema = z.object({
  status: z.nativeEnum(CashSaleStatus).optional(),
  
  contract_type: z.string().optional(),
  contract_number: z.string().optional(),
  contract_release: z.string().optional(),
  
  location_id: z.string().uuid().optional().nullable(),
  customer_id: z.string().uuid().optional(),
  market_zone_id: z.string().uuid().optional().nullable(),
  trader_id: z.string().uuid().optional().nullable(),

  destination: z.string().optional().nullable(),
  washed_gain_loss: z.string().optional().nullable(),
  
  add_date: z.string().optional(), // YYYY-MM-DD format
  start_date: z.string().optional().nullable(), // YYYY-MM-DD format
  end_date: z.string().optional().nullable(), // YYYY-MM-DD format
  
  note: z.string().optional().nullable(),
  commodity: z.nativeEnum(AcceptedCommodity).optional(),
  crop_year: z.number().optional(),
  
  option_month: z.string().optional().nullable(),
  
  loads: z.string().optional().nullable(),
  
  quantity: z.string(),
  scheduled_quantity: z.string().optional().nullable(),
  balance: z.string().optional().nullable(),
  futures: z.string().optional().nullable(),
  basis: z.string().optional().nullable(),
  price: z.string().optional().nullable(),
});

export const ListCashSalesQuerySchema = z.object({
  crop_year: z.coerce.number().optional(),
  customer_id: z.string().uuid().optional(),
  commodity_type: z.string().optional(),
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(50)
});

export const ListClientPositionsQuerySchema = z.object({
  crop_year: z.coerce.number().optional(),
  customer_id: z.string().uuid().optional(),
  commodity: z.string().optional(),
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(50)
});

// Types
export interface ManualCashSale extends BaseDbObject {
  source_type?: string;
  contract_number: string;
  contract_release: string;
  price: string;
  commodity: string;
  quantity: string;
  customer_id: string;
  expiry_date: string;
  option_month?: string;
  note?: string;
  crop_year?: number;
}

export interface ManualClientPosition extends BaseDbObject {
  source_type?: string;
  contract_number: string;
  contract_release: string;
  price: string;
  commodity: string;
  quantity: string;
  customer_id: string;
  trading_account_id: string;
  expiry_date: string;
  option_month?: string;
  note?: string;
  crop_year?: number;
}

export interface ListCashSalesResult {
  data: ManualCashSale[];
  total: number;
  page: number;
  limit: number;
}

export interface ListClientPositionsResult {
  data: ManualClientPosition[];
  total: number;
  page: number;
  limit: number;
}

// Cash Sales Hooks
export function useCreateManualCashSale() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateCashSaleSchema>) => {
    const result = await fetchApiPost<ManualCashSale>(
      "risk/manual-entries/cash-sales",
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["manual-cash-sales"] });
      queryClient.invalidateQueries({ queryKey: ["risk", "cash_sales"] });
    }

    return result;
  };
}

export function useListManualCashSales(query?: z.infer<typeof ListCashSalesQuerySchema>) {
  const queryParams = new URLSearchParams();

  if (query) {
    if (query.crop_year !== undefined) {
      queryParams.set("crop_year", query.crop_year.toString());
    }
    if (query.customer_id) {
      queryParams.set("customer_id", query.customer_id);
    }
    if (query.commodity_type) {
      queryParams.set("commodity_type", query.commodity_type);
    }
    if (query.page) {
      queryParams.set("page", query.page.toString());
    }
    if (query.limit) {
      queryParams.set("limit", query.limit.toString());
    }
  }

  return useQuery({
    queryKey: ["risk", "manual-cash-sales", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<ListCashSalesResult>("risk/manual-entries/cash-sales", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useGetManualCashSale(id: string) {
  return useQuery({
    queryKey: ["risk", "manual-cash-sales", id],
    queryFn: () =>
      fetchApiGet<ManualCashSale>(`risk/manual-entries/cash-sales/${id}`),
    enabled: !!id,
    retry: 2,
  });
}

export function useUpdateManualCashSale() {
  const queryClient = useQueryClient();

  return async (id: string, data: z.infer<typeof UpdateCashSaleSchema>) => {
    const result = await fetchApiPut<ManualCashSale>(
      `risk/manual-entries/cash-sales/${id}`,
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }

    return result;
  };
}

export function useDeleteManualCashSale() {
  const queryClient = useQueryClient();

  return async (id: string) => {
    const result = await fetchApiDelete<{}>(
      `risk/manual-entries/cash-sales/${id}`,
      new URLSearchParams()
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }

    return result;
  };
}

// Client Positions Hooks
export function useCreateManualClientPosition() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof manualPosBaseSchema>) => {
    const result = await fetchApiPost<ManualClientPosition>(
      "risk/manual-entries/client-positions",
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }

    return result;
  };
}

export function useListManualClientPositions(query?: z.infer<typeof ListClientPositionsQuerySchema>) {
  const queryParams = new URLSearchParams();

  if (query) {
    if (query.crop_year !== undefined) {
      queryParams.set("crop_year", query.crop_year.toString());
    }
    if (query.customer_id) {
      queryParams.set("customer_id", query.customer_id);
    }
    if (query.commodity) {
      queryParams.set("commodity", query.commodity);
    }
    if (query.page) {
      queryParams.set("page", query.page.toString());
    }
    if (query.limit) {
      queryParams.set("limit", query.limit.toString());
    }
  }

  return useQuery({
    queryKey: ["risk", "manual-client-positions", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<ListClientPositionsResult>("risk/manual-entries/client-positions", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useGetManualClientPosition(id: string) {
  return useQuery({
    queryKey: ["risk", "manual-client-positions", id],
    queryFn: () =>
      fetchApiGet<ManualClientPosition>(`risk/manual-entries/client-positions/${id}`),
    enabled: !!id,
    retry: 2,
  });
}

export function useUpdateManualClientPosition() {
  const queryClient = useQueryClient();

  return async (id: string, data: z.infer<typeof manualPosBaseSchema>) => {
    const result = await fetchApiPut<ManualClientPosition>(
      `risk/manual-entries/client-positions/${id}`,
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }

    return result;
  };
}

export function useDeleteManualClientPosition() {
  const queryClient = useQueryClient();

  return async (id: string) => {
    const result = await fetchApiDelete<{}>(
      `risk/manual-entries/client-positions/${id}`,
      new URLSearchParams()
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }

    return result;
  };
}

export function usePriceManualCashSale() {
  const queryClient = useQueryClient();

  return async (id: string, data: z.infer<typeof PriceCashSaleSchema>) => {
    const result = await fetchApiPost<ManualClientPosition>(
      `risk/manual-entries/cash-sales/${id}/price`,
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }

    return result;
  };
}
